/**
 * FinanzSteuerberaterPage — Sektion „Steuerberater" im Finanzen-Cockpit
 * (Route /finanzen/:firma/steuerberater)
 *
 * Eingebettet im Mandanten-Cockpit. Stellt die Steuerberater-Kanzleien (alt /
 * Regress / aktuell), die zugehörigen Kosten (alt vs. aktuell) sowie das
 * Regress-Dossier gegen den alten Steuerberater dar. Ruhig gehalten
 * (slate/zinc), ein einziger emerald-Akzent, rose nur für Warnungen.
 *
 * Prop `firmaKey`:
 *   'sv'   -> SV Büro    (firma = 'sv_buero_koop')
 *   'lcs'  -> LCS        (firma = 'lcs')
 *   'alle' -> alle Firmen (keine Firma-Einschränkung)
 *
 * DATENQUELLEN (read-only Tabellen, bereits vorhanden — keine Migration):
 *   - stb_kanzlei:  id, name, rolle ('alt'|'alt_regress'|'aktuell'), zeitraum,
 *                   mandate, ansprechpartner, notiz
 *   - stb_kosten:   id, kanzlei, firma ('sv_buero_koop'|'lcs'|'privat'|null),
 *                   zeitraum, leistung, betrag, bezahlt, geliefert
 *                   ('erbracht'|'teilweise'|'nicht_erbracht'), notiz
 *   - stb_regress:  id, position, titel, betrag, beweiskraft, status, firma,
 *                   beschreibung
 *
 * Alle Abfragen sind robust gegen fehlende Tabellen/leere Daten (kein Absturz).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Scale, Loader2, Inbox, History, ReceiptText, Gavel, CheckCircle2,
  AlertTriangle, Building2,
} from 'lucide-react';

// ============================================================
// Firmen-Mapping
// ============================================================

export type SteuerberaterFirmaKey = 'sv' | 'lcs' | 'alle';

/** firmaKey -> konkreter firma-String (für Filter). 'alle' = keine Einschränkung. */
const FIRMA_STRING: Record<Exclude<SteuerberaterFirmaKey, 'alle'>, string> = {
  sv: 'sv_buero_koop',
  lcs: 'lcs',
};

const FIRMA_LABEL: Record<string, string> = {
  sv_buero_koop: 'SV Büro',
  lcs: 'LCS',
  privat: 'Privat',
};

// ============================================================
// Typen
// ============================================================

type KanzleiRolle = 'alt' | 'alt_regress' | 'aktuell';

interface Kanzlei {
  id: string | number;
  name: string | null;
  rolle: KanzleiRolle | string | null;
  zeitraum: string | null;
  mandate: string | null;
  ansprechpartner: string | null;
  notiz: string | null;
}

type Geliefert = 'erbracht' | 'teilweise' | 'nicht_erbracht';

interface Kosten {
  id: string | number;
  kanzlei: string | null;
  firma: string | null;
  zeitraum: string | null;
  leistung: string | null;
  betrag: number | string | null;
  bezahlt: boolean | null;
  geliefert: Geliefert | string | null;
  notiz: string | null;
}

type Beweiskraft = 'stark' | 'mittel-stark' | 'mittel' | 'schwach' | 'offen';

interface Regress {
  id: string | number;
  position: string | null;
  titel: string | null;
  betrag: number | string | null;
  beweiskraft: Beweiskraft | string | null;
  status: string | null;
  firma: string | null;
  beschreibung: string | null;
}

type TabId = 'historie' | 'kosten' | 'regress';

// ============================================================
// Helpers
// ============================================================

function isTableNotFound(error: unknown): boolean {
  if (!error) return false;
  const e = error as { message?: string; code?: string };
  const msg = (e.message || '') + ' ' + (e.code || '');
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

function toNum(v: number | string | null | undefined): number {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtEuro(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function firmaLabel(f: string | null): string {
  if (!f) return 'Alle / Übergreifend';
  return FIRMA_LABEL[f] || f;
}

// ============================================================
// Daten-Layer
// ============================================================

async function ladeKanzleien(): Promise<Kanzlei[]> {
  try {
    const { data, error } = await supabase
      .from('stb_kanzlei')
      .select('id, name, rolle, zeitraum, mandate, ansprechpartner, notiz')
      .limit(1000);
    if (error) throw error;
    return (data as Kanzlei[]) || [];
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Steuerberater: Kanzleien laden fehlgeschlagen:', err);
    return [];
  }
}

async function ladeKosten(firmaKey: SteuerberaterFirmaKey): Promise<Kosten[]> {
  try {
    const { data, error } = await supabase
      .from('stb_kosten')
      .select('id, kanzlei, firma, zeitraum, leistung, betrag, bezahlt, geliefert, notiz')
      .limit(10000);
    if (error) throw error;
    let rows = (data as Kosten[]) || [];
    // Bei firmaKey != 'alle': nach firma filtern, Zeilen mit firma null immer zeigen.
    if (firmaKey !== 'alle') {
      const ziel = FIRMA_STRING[firmaKey];
      rows = rows.filter((r) => r.firma == null || r.firma === ziel);
    }
    return rows;
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Steuerberater: Kosten laden fehlgeschlagen:', err);
    return [];
  }
}

async function ladeRegress(): Promise<Regress[]> {
  try {
    const { data, error } = await supabase
      .from('stb_regress')
      .select('id, position, titel, betrag, beweiskraft, status, firma, beschreibung')
      .limit(10000);
    if (error) throw error;
    return (data as Regress[]) || [];
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Steuerberater: Regress laden fehlgeschlagen:', err);
    return [];
  }
}

// ============================================================
// UI-Bausteine
// ============================================================

/** Dezentes Badge (slate). */
function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
      {children}
    </span>
  );
}

/** Rollen-Badge: aktuell emerald, alt_regress rose, alt slate. */
function RolleBadge({ rolle }: { rolle: string | null }) {
  if (rolle === 'aktuell') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
        Aktuell
      </span>
    );
  }
  if (rolle === 'alt_regress') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
        Regress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
      Alt
    </span>
  );
}

/** Geliefert-Badge: erbracht emerald, teilweise amber, nicht_erbracht rose. */
function GeliefertBadge({ wert }: { wert: string | null }) {
  if (wert === 'erbracht') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
        Erbracht
      </span>
    );
  }
  if (wert === 'teilweise') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
        Teilweise
      </span>
    );
  }
  if (wert === 'nicht_erbracht') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200">
        Nicht erbracht
      </span>
    );
  }
  return <span className="text-slate-300">—</span>;
}

/** Beweiskraft-Badge: stark emerald, mittel amber, schwach grau, offen slate. */
function BeweiskraftBadge({ wert }: { wert: string | null }) {
  const w = (wert || '').toLowerCase();
  if (w === 'stark' || w === 'mittel-stark') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
        {wert}
      </span>
    );
  }
  if (w === 'mittel') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
        mittel
      </span>
    );
  }
  if (w === 'schwach') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-zinc-100 text-zinc-500 border border-zinc-200">
        schwach
      </span>
    );
  }
  if (w === 'offen') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
        offen
      </span>
    );
  }
  return <span className="text-slate-300">—</span>;
}

/** Tab-Umschalter. */
function TabButton({
  aktiv, onClick, icon, children,
}: {
  aktiv: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
        aktiv
          ? 'bg-emerald-600 text-white border-emerald-600'
          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

/** Leerer-Zustand-Block. */
function LeerBlock({ titel, text }: { titel: string; text: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
      <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center mx-auto mb-4">
        <Inbox className="w-6 h-6 text-slate-300" />
      </div>
      <h3 className="text-base font-bold text-slate-900">{titel}</h3>
      <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto leading-relaxed">{text}</p>
    </div>
  );
}

// ============================================================
// Abschnitt A — Kanzlei-Historie
// ============================================================

function HistorieTab({ kanzleien }: { kanzleien: Kanzlei[] }) {
  // Sortierung: aktuell zuerst, dann alt_regress, dann alt; innerhalb nach Name.
  const sortiert = useMemo(() => {
    const rang: Record<string, number> = { aktuell: 0, alt_regress: 1, alt: 2 };
    return kanzleien.slice().sort((a, b) => {
      const ra = rang[String(a.rolle)] ?? 9;
      const rb = rang[String(b.rolle)] ?? 9;
      if (ra !== rb) return ra - rb;
      return (a.name || '').localeCompare(b.name || '', 'de');
    });
  }, [kanzleien]);

  if (sortiert.length === 0) {
    return (
      <LeerBlock
        titel="Keine Kanzleien hinterlegt"
        text="Es sind noch keine Steuerberater-Kanzleien erfasst."
      />
    );
  }

  return (
    <div className="space-y-3">
      {sortiert.map((k) => {
        const rahmen =
          k.rolle === 'aktuell'
            ? 'bg-emerald-50/60 border-emerald-200'
            : k.rolle === 'alt_regress'
              ? 'bg-rose-50/50 border-rose-200'
              : 'bg-white border-slate-200';
        return (
          <div key={String(k.id)} className={`rounded-xl border p-4 ${rahmen}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-slate-500" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">{k.name || 'Unbenannte Kanzlei'}</h3>
                  {k.zeitraum && <p className="text-xs text-slate-500 mt-0.5">{k.zeitraum}</p>}
                </div>
              </div>
              <RolleBadge rolle={k.rolle ?? null} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3 text-xs">
              {k.mandate && (
                <div>
                  <span className="text-slate-400">Mandate: </span>
                  <span className="text-slate-700">{k.mandate}</span>
                </div>
              )}
              {k.ansprechpartner && (
                <div>
                  <span className="text-slate-400">Ansprechpartner: </span>
                  <span className="text-slate-700">{k.ansprechpartner}</span>
                </div>
              )}
            </div>
            {k.notiz && (
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">{k.notiz}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// Abschnitt B — Kosten (gruppiert nach Kanzlei)
// ============================================================

function KostenTab({ kosten }: { kosten: Kosten[] }) {
  // Gruppierung nach Kanzlei, Reihenfolge nach erstem Auftreten.
  const gruppen = useMemo(() => {
    const map = new Map<string, Kosten[]>();
    for (const k of kosten) {
      const key = k.kanzlei || 'Ohne Kanzlei';
      const arr = map.get(key);
      if (arr) arr.push(k);
      else map.set(key, [k]);
    }
    return Array.from(map.entries()).map(([kanzlei, rows]) => ({
      kanzlei,
      rows,
      summe: rows.reduce((s, r) => s + toNum(r.betrag), 0),
    }));
  }, [kosten]);

  const gesamt = useMemo(() => kosten.reduce((s, r) => s + toNum(r.betrag), 0), [kosten]);

  if (kosten.length === 0) {
    return (
      <LeerBlock
        titel="Keine Kosten hinterlegt"
        text="Für die gewählte Firma liegen keine Steuerberater-Kosten vor."
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gesamtkosten</div>
        <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{fmtEuro(gesamt)}</div>
        <div className="text-xs text-slate-400 mt-0.5">{kosten.length} Position(en) über {gruppen.length} Kanzlei(en)</div>
      </div>

      {gruppen.map((g) => (
        <div key={g.kanzlei} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2.5">
              <ReceiptText className="w-4 h-4 text-slate-500" />
              <h3 className="text-sm font-bold text-slate-900">{g.kanzlei}</h3>
            </div>
            <div className="text-xs text-slate-500">
              Summe <span className="font-bold text-slate-800 tabular-nums">{fmtEuro(g.summe)}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Firma</th>
                  <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Zeitraum</th>
                  <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5">Leistung</th>
                  <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Betrag</th>
                  <th className="text-center font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Bezahlt</th>
                  <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Geliefert</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r) => (
                  <tr key={String(r.id)} className="border-t border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{firmaLabel(r.firma)}</td>
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{r.zeitraum || '—'}</td>
                    <td className="px-4 py-2.5 text-slate-700 min-w-[200px]">
                      <span className="line-clamp-2">{r.leistung || '—'}</span>
                      {r.notiz && <span className="block text-xs text-slate-400 mt-0.5 line-clamp-1">{r.notiz}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-900 whitespace-nowrap">{fmtEuro(toNum(r.betrag))}</td>
                    <td className="px-4 py-2.5 text-center whitespace-nowrap">
                      {r.bezahlt ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> ja
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
                          nein
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <GeliefertBadge wert={r.geliefert ?? null} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50/60">
                  <td className="px-4 py-2.5 text-xs font-semibold text-slate-500" colSpan={3}>Summe {g.kanzlei}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-bold text-slate-900 whitespace-nowrap">{fmtEuro(g.summe)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Abschnitt C — Regress-Dossier
// ============================================================

function RegressTab({ regress }: { regress: Regress[] }) {
  const sortiert = useMemo(
    () =>
      regress.slice().sort((a, b) =>
        (a.position || '').localeCompare(b.position || '', 'de', { numeric: true }),
      ),
    [regress],
  );

  // Belegte Schadenssumme = Summe der betrag wo status like 'belegt%'.
  const belegteSumme = useMemo(
    () =>
      regress.reduce(
        (s, r) => (String(r.status || '').startsWith('belegt') ? s + toNum(r.betrag) : s),
        0,
      ),
    [regress],
  );

  if (sortiert.length === 0) {
    return (
      <LeerBlock
        titel="Kein Regress-Dossier"
        text="Es sind noch keine Regress-Positionen gegen den alten Steuerberater erfasst."
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Belegte Schadenssumme */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Belegte Schadenssumme</div>
          <Gavel className="w-4 h-4 text-slate-400" />
        </div>
        <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{fmtEuro(belegteSumme)}</div>
        <div className="text-xs text-slate-400 mt-0.5">Summe der Positionen mit Status „belegt…"</div>
      </div>

      {/* Dossier-Tabelle */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 w-16 whitespace-nowrap">Pos.</th>
                <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5">Titel / Beschreibung</th>
                <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Betrag</th>
                <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Beweiskraft</th>
                <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortiert.map((r) => (
                <tr key={String(r.id)} className="border-t border-slate-50 hover:bg-slate-50/50 align-top">
                  <td className="px-4 py-3 font-bold text-slate-700 whitespace-nowrap">{r.position || '—'}</td>
                  <td className="px-4 py-3 min-w-[260px]">
                    <div className="font-medium text-slate-900">{r.titel || '—'}</div>
                    {r.beschreibung && (
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{r.beschreibung}</p>
                    )}
                    {r.firma && (
                      <span className="inline-block mt-1.5"><Badge>{firmaLabel(r.firma)}</Badge></span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900 whitespace-nowrap">
                    {r.betrag == null || r.betrag === '' ? (
                      <span className="text-slate-400 font-normal">offen</span>
                    ) : (
                      fmtEuro(toNum(r.betrag))
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <BeweiskraftBadge wert={r.beweiskraft ?? null} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.status ? <Badge>{r.status}</Badge> : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hinweis */}
      <div className="flex items-start gap-2 text-xs text-slate-400">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>Arbeitsstand, keine Rechtsberatung — anwaltlich zu prüfen.</span>
      </div>
    </div>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================

export default function FinanzSteuerberaterPage({ firmaKey = 'sv' }: { firmaKey?: SteuerberaterFirmaKey }) {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabId>('historie');
  const [kanzleien, setKanzleien] = useState<Kanzlei[]>([]);
  const [kosten, setKosten] = useState<Kosten[]>([]);
  const [regress, setRegress] = useState<Regress[]>([]);

  const lade = useCallback(async () => {
    setLoading(true);
    const [ka, ko, re] = await Promise.all([
      ladeKanzleien(),
      ladeKosten(firmaKey),
      ladeRegress(),
    ]);
    setKanzleien(ka);
    setKosten(ko);
    setRegress(re);
    setLoading(false);
  }, [firmaKey]);

  useEffect(() => {
    lade();
  }, [lade]);

  return (
    <div className="space-y-5">
      {/* Kopf */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
            <Scale className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Steuerberater</h2>
            <p className="text-xs text-slate-400">Kanzlei-Historie, Kosten &amp; Regress-Dossier</p>
          </div>
        </div>
        {!loading && (
          <div className="text-xs text-slate-400">
            {kanzleien.length} Kanzlei(en) · {kosten.length} Kostenposition(en) · {regress.length} Regress-Posten
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <TabButton
          aktiv={tab === 'historie'}
          onClick={() => setTab('historie')}
          icon={<History className="w-4 h-4" />}
        >
          Kanzlei-Historie
        </TabButton>
        <TabButton
          aktiv={tab === 'kosten'}
          onClick={() => setTab('kosten')}
          icon={<ReceiptText className="w-4 h-4" />}
        >
          Kosten
        </TabButton>
        <TabButton
          aktiv={tab === 'regress'}
          onClick={() => setTab('regress')}
          icon={<Gavel className="w-4 h-4" />}
        >
          Regress-Dossier{regress.length > 0 ? ` (${regress.length})` : ''}
        </TabButton>
      </div>

      {/* Inhalt */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {tab === 'historie' && <HistorieTab kanzleien={kanzleien} />}
          {tab === 'kosten' && <KostenTab kosten={kosten} />}
          {tab === 'regress' && <RegressTab regress={regress} />}
        </>
      )}
    </div>
  );
}
